import { useState } from "react";
import { ShieldCheck, ShieldAlert, Shield, Sparkles, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { analyzeApplicationFraud, type AIFraudScore } from "@/project/lib/ai-engine";

interface Props {
  application: {
    org_name: string;
    reg_80g?: string;
    reg_12a?: string;
    registration_no: string;
    animals_cared: number;
    about?: string;
    city: string;
  };
}

export function AIFraudDetector({ application }: Props) {
  const [result, setResult] = useState<AIFraudScore | null>(null);
  const [loading, setLoading] = useState(false);

  async function analyze() {
    setLoading(true);
    setResult(null);
    try {
      const r = await analyzeApplicationFraud(application);
      setResult(r);
    } finally {
      setLoading(false);
    }
  }

  const riskConfig = result
    ? result.riskLevel === "low"
      ? { color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200", Icon: ShieldCheck }
      : result.riskLevel === "medium"
      ? { color: "text-amber-600", bg: "bg-amber-50 border-amber-200", Icon: Shield }
      : { color: "text-red-600", bg: "bg-red-50 border-red-200", Icon: ShieldAlert }
    : null;

  return (
    <div className="rounded-2xl border border-border/80 bg-white overflow-hidden shadow-xs">
      <div className="flex items-center gap-3 border-b border-border/60 bg-[var(--cream-2)] px-4 py-3.5">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--saffron)]/20 text-[var(--saffron-deep)]">
          <ShieldCheck className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold text-foreground">AI Fraud Detector</p>
          <p className="text-[11px] text-muted-foreground">Automated risk analysis for partner applications</p>
        </div>
        <span className="rounded-full bg-[var(--saffron)]/15 border border-[var(--saffron-deep)]/30 px-2.5 py-0.5 text-[10px] font-bold text-[var(--saffron-deep)] flex items-center gap-1">
          <Sparkles className="h-3 w-3" /> AI Verification
        </span>
      </div>

      <div className="bg-white p-4 space-y-3">
        {!result && !loading && (
          <button
            onClick={analyze}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-slate-900 py-3 text-sm font-bold text-white hover:bg-slate-800 transition shadow-xs cursor-pointer"
          >
            <Sparkles className="h-4 w-4 text-[var(--saffron)]" /> Run AI Risk Analysis
          </button>
        )}

        {loading && (
          <div className="flex flex-col items-center py-6 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-slate-800" />
            <div className="text-center">
              <p className="font-bold text-sm text-foreground">Analysing application...</p>
              <p className="text-xs text-muted-foreground mt-0.5">Checking certificates and signals</p>
            </div>
          </div>
        )}

        {result && !loading && riskConfig && (
          <div className="space-y-3">
            <div className={`rounded-xl border p-4 ${riskConfig.bg}`}>
              <div className="flex items-center gap-3">
                <div className="relative h-14 w-14 shrink-0">
                  <svg viewBox="0 0 36 36" className="h-14 w-14 -rotate-90">
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-black/10" />
                    <circle
                      cx="18"
                      cy="18"
                      r="15.9"
                      fill="none"
                      strokeWidth="2.5"
                      className={
                        result.riskLevel === "low"
                          ? "text-emerald-500"
                          : result.riskLevel === "medium"
                          ? "text-amber-500"
                          : "text-red-500"
                      }
                      stroke="currentColor"
                      strokeDasharray={`${result.score} ${100 - result.score}`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-sm font-bold">
                    {result.score}
                  </span>
                </div>

                <div>
                  <div className="flex items-center gap-1.5">
                    <riskConfig.Icon className={`h-4 w-4 ${riskConfig.color}`} />
                    <p className={`font-bold text-sm capitalize ${riskConfig.color}`}>
                      {result.riskLevel} Risk
                    </p>
                  </div>
                  <p className="text-xs text-foreground/75 mt-0.5">{result.recommendation}</p>
                </div>
              </div>
            </div>

            <div className="space-y-1.5 border-t border-border/60 pt-3 text-xs">
              <p className="font-bold text-muted-foreground uppercase tracking-wider text-[10px]">Verification Signals</p>
              {result.signals.map((sig, idx) => (
                <div key={idx} className="flex items-center gap-2 text-foreground/80">
                  {sig.includes("Verified") || sig.includes("valid") ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                  ) : (
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                  )}
                  <span>{sig}</span>
                </div>
              ))}
            </div>

            <button
              onClick={analyze}
              className="w-full text-center text-xs font-semibold text-muted-foreground hover:text-foreground pt-1"
            >
              Re-run Risk Analysis
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
